import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { useStreamGenerate } from "@/hooks/useStreamGenerate";
import { renderMarkdown } from "@/lib/markdown";
import {
  createWatch,
  deleteWatch,
  listWatches,
  updateWatch,
  validateWatchDraft,
  type Watch,
} from "@/lib/watchesApi";
import { STORAGE_LEADS } from "@/constants/storage";

// Leads still live in localStorage on this page — that surface predates
// the CRM API and is out of scope for PROP-29 (watches-only). When CRM
// reads land, this constant + the inline useMemo lookup below should
// move to a leadsApi.ts client similar to watchesApi.ts.
const LEADS_KEY = STORAGE_LEADS.crmLeads;

// Domain enums kept in English (these are stable identifiers; the UI
// renders them via t("marketWatch.flatType.<v>", { defaultValue: <v> })
// so a translation key only takes over the display, not the value.)
const FLAT_TYPES = [
  "2-room HDB", "3-room HDB", "4-room HDB", "5-room HDB", "Executive HDB",
  "1BR Condo", "2BR Condo", "3BR Condo", "4BR Condo",
  "Landed",
];
const ALERT_PCTS = [3, 5, 8, 10];

export function MarketWatch() {
  const { t } = useTranslation();
  const [watches, setWatches] = useState<Watch[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [modal, setModal] = useState(false);

  // Initial hydration from /watches. Failure (anonymous user, network
  // hiccup) collapses to an empty list — the empty-state CTA still
  // renders so the page is never visibly broken.
  useEffect(() => {
    let cancelled = false;
    listWatches()
      .then((rows) => { if (!cancelled) setWatches(rows); })
      .catch(() => { if (!cancelled) setWatches([]); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // createWatch round-trip: the server-returned row carries the
  // canonical id + timestamps so optimistic state matches a post-refresh
  // hydrate exactly (per CLAUDE.md "optimistic UI must equal post-refresh
  // UI"). We don't insert a temp id locally — waiting on the response is
  // a sub-second hop and avoids id-reconciliation churn.
  async function add(input: { area: string; flat_type: string; alert_pct: number; min_price?: number; max_price?: number }) {
    // PROP-190: let failures propagate. The modal keeps the user's typed
    // input and surfaces the error inline instead of closing on a rejected
    // save — createWatch throws on a 4xx (e.g. the server rejects
    // max_price < min_price with 400).
    const row = await createWatch(input);
    setWatches((list) => [row, ...list]);
  }

  // update is invoked from WatchCard with the row already mutated
  // client-side (e.g. {...watch, summary, last_checked}). We extract the
  // changed columns and PATCH; the server response replaces the row so
  // updated_at + any server-side normalisation lands cleanly.
  async function update(next: Watch) {
    const prev = watches.find((x) => x.id === next.id);
    if (!prev) return;
    const patch: Parameters<typeof updateWatch>[1] = {};
    if (next.area !== prev.area) patch.area = next.area;
    if (next.flat_type !== prev.flat_type) patch.flat_type = next.flat_type;
    if (next.alert_pct !== prev.alert_pct) patch.alert_pct = next.alert_pct;
    if (next.min_price !== prev.min_price) patch.min_price = next.min_price ?? null;
    if (next.max_price !== prev.max_price) patch.max_price = next.max_price ?? null;
    if (next.summary !== prev.summary) patch.summary = next.summary;
    if (next.last_checked && next.last_checked !== prev.last_checked) patch.last_checked = next.last_checked;
    if (Object.keys(patch).length === 0) return;
    try {
      const row = await updateWatch(next.id, patch);
      setWatches((list) => list.map((x) => (x.id === row.id ? row : x)));
    } catch {
      // Refresh from server so the UI doesn't lie about persisted state.
      listWatches().then(setWatches).catch(() => undefined);
    }
  }

  async function remove(id: string) {
    if (!confirm(t("marketWatch.confirmRemove"))) return;
    // Optimistic remove keeps the UI snappy; on failure we re-fetch so
    // the deleted-but-failed row reappears rather than silently lying.
    const snapshot = watches;
    setWatches((list) => list.filter((w) => w.id !== id));
    try {
      await deleteWatch(id);
    } catch {
      setWatches(snapshot);
      alert(t("marketWatch.deleteFailed", { defaultValue: "Couldn't delete watch — please try again." }));
    }
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t("marketWatch.title")}
        subtitle={t("marketWatch.subtitle")}
        actions={<>
          <button className="btn-primary" onClick={() => setModal(true)}>{t("marketWatch.addArea")}</button>
        </>}
      />
      <PageBody className="flex-1 overflow-y-auto">
        {loaded && watches.length === 0 ? (
          <div className="card p-8 text-center text-[13px] text-text-muted">
            {t("marketWatch.emptyPrefix")}{" "}
            <strong>{t("marketWatch.addArea")}</strong>{" "}
            {t("marketWatch.emptySuffix")}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {watches.map((w) => (
              <WatchCard key={w.id} watch={w} onUpdate={update} onDelete={() => remove(w.id)} />
            ))}
          </div>
        )}
      </PageBody>
      {modal && <AddWatchModal onClose={() => setModal(false)} onSave={async (input) => { await add(input); setModal(false); }} />}
    </div>
  );
}

function WatchCard({ watch, onUpdate, onDelete }: {
  watch: Watch;
  onUpdate: (w: Watch) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const gen = useStreamGenerate();
  const leads = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(LEADS_KEY) || "[]") as Array<{ name: string; preferred_areas?: string; property_type?: string }>; } catch { return []; }
  }, []);
  const matching = leads.filter((l) => (l.preferred_areas || "").toLowerCase().includes(watch.area.toLowerCase()));

  async function refresh() {
    // Prompt is sent to the LLM — keep English (model performs better
    // and the streamed output respects the user's language naturally).
    const prompt = `Generate a weekly rental market summary for ${watch.flat_type} in ${watch.area}, Singapore.
Include: Market Overview, Rental Price Analysis, Quarterly Trend, Top Projects, Investment Analysis, Recommendations (landlords/tenants/investors).
Alert threshold: ±${watch.alert_pct}%.
${watch.min_price || watch.max_price ? `Price band: S$${watch.min_price || "?"} – S$${watch.max_price || "?"}.` : ""}`;
    // Guard the stream: gen.run can reject (provider failure, cancel,
    // network) and would otherwise let `undefined` flow into onUpdate
    // and clobber the persisted summary. gen.error already surfaces
    // the failure in the card, so we just need to NOT write garbage.
    let text: string;
    try {
      text = await gen.run([{ role: "user", content: prompt }], { mode: "agent" });
    } catch {
      return;
    }
    if (!text) return;
    onUpdate({ ...watch, summary: text, last_checked: new Date().toISOString() });
  }

  const flatLabel = t(`marketWatch.flatType.${watch.flat_type}`, { defaultValue: watch.flat_type });

  return (
    <div className="card space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[13px] text-text-muted">{flatLabel}</div>
          <h3 className="text-lg font-bold">{watch.area}</h3>
          <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
            <span className="chip">{t("marketWatch.alertChip", { pct: watch.alert_pct })}</span>
            {watch.min_price && <span className="chip">{t("marketWatch.minChip", { val: watch.min_price.toLocaleString() })}</span>}
            {watch.max_price && <span className="chip">{t("marketWatch.maxChip", { val: watch.max_price.toLocaleString() })}</span>}
            <span className="chip">{t("marketWatch.leadsChip", { count: matching.length })}</span>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <button className="btn-primary !px-3 !py-1 !text-[12px]" onClick={refresh} disabled={gen.streaming}>
            {gen.streaming ? t("marketWatch.analyzing") : watch.summary ? t("marketWatch.refresh") : t("marketWatch.generate")}
          </button>
          <button className="btn-ghost !px-3 !py-1 !text-[12px] !text-bad" onClick={onDelete}>{t("marketWatch.removeBtn")}</button>
        </div>
      </div>

      {gen.error && <div className="rounded border border-bad/30 bg-bad/5 p-2 text-[12px] text-bad">{gen.error}</div>}
      {(gen.text || watch.summary) && (
        <div className="markdown rounded-md border border-border-subtle bg-surface-2 p-3 text-[13px]"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(gen.text || watch.summary || "") + (gen.streaming ? "▌" : "") }} />
      )}

      {matching.length > 0 && (
        <div>
          <div className="label-text">{t("marketWatch.leadsHeader")}</div>
          <div className="flex flex-wrap gap-1.5">
            {matching.slice(0, 8).map((l, i) => <span key={i} className="chip">{l.name}</span>)}
          </div>
        </div>
      )}

      {watch.last_checked && (
        <div className="text-[11px] text-text-muted">{t("marketWatch.lastChecked", { ts: new Date(watch.last_checked).toLocaleString() })}</div>
      )}
    </div>
  );
}

function AddWatchModal({ onClose, onSave }: {
  onClose: () => void;
  // onSave returns a Promise so the modal can disable the Save button
  // while the create round-trip is in flight. The parent closes the
  // modal once the server row lands; we no longer mint a client-side
  // id (the server returns the canonical one).
  onSave: (input: { area: string; flat_type: string; alert_pct: number; min_price?: number; max_price?: number }) => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const [area, setArea] = useState("");
  const [flat, setFlat] = useState(FLAT_TYPES[2]);
  const [pct, setPct] = useState(5);
  const [mn, setMn] = useState("");
  const [mx, setMx] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    // Validate client-side first (mirrors the server's area + price-range
    // guards) so common mistakes show instantly without a round-trip.
    const draft = validateWatchDraft({
      area,
      flat_type: flat,
      alert_pct: pct,
      min_price: mn ? Number(mn) : undefined,
      max_price: mx ? Number(mx) : undefined,
    });
    if (!draft.ok) {
      setErr(
        draft.error === "priceRangeInvalid"
          ? (t("marketWatch.priceRangeInvalid", {
              defaultValue: "Max price must be at least the min price.",
            }) as string)
          : (t("marketWatch.areaRequired") as string),
      );
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await onSave(draft.value);
      // Success path: the parent closes the modal. We deliberately do NOT
      // close on error — onSave rejects on a failed save (PROP-190), so
      // the catch keeps the modal open with the user's input intact.
    } catch {
      setErr(
        t("marketWatch.saveFailed", {
          defaultValue: "Couldn't save watch — please try again.",
        }) as string,
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/70 p-3 backdrop-blur-sm animate-fade-in" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface shadow-2xl animate-slide-in">
        <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <h2 className="text-base font-bold">{t("marketWatch.modalTitle")}</h2>
          <button className="btn-ghost !px-2" onClick={onClose}>×</button>
        </div>
        <div className="space-y-2 px-4 py-3">
          <label className="block"><span className="label-text">{t("marketWatch.areaLabel")}</span>
            <input className="input-field" value={area} onChange={(e) => setArea(e.target.value)} placeholder={t("marketWatch.areaPlaceholder") as string} />
          </label>
          <label className="block"><span className="label-text">{t("marketWatch.flatTypeLabel")}</span>
            <select className="input-field" value={flat} onChange={(e) => setFlat(e.target.value)}>
              {FLAT_TYPES.map((ft) => (
                <option key={ft} value={ft}>
                  {t(`marketWatch.flatType.${ft}`, { defaultValue: ft })}
                </option>
              ))}
            </select>
          </label>
          <label className="block"><span className="label-text">{t("marketWatch.thresholdLabel")}</span>
            <select className="input-field" value={pct} onChange={(e) => setPct(Number(e.target.value))}>
              {ALERT_PCTS.map((p) => <option key={p} value={p}>±{p}%</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="label-text">{t("marketWatch.minPriceLabel")}</span>
              <input type="number" className="input-field" value={mn} onChange={(e) => setMn(e.target.value)} />
            </label>
            <label className="block"><span className="label-text">{t("marketWatch.maxPriceLabel")}</span>
              <input type="number" className="input-field" value={mx} onChange={(e) => setMx(e.target.value)} />
            </label>
          </div>
        </div>
        {err && (
          <p className="px-4 pb-1 text-sm text-rose-700" role="alert">
            {err}
          </p>
        )}
        <div className="flex items-center justify-end gap-2 border-t border-border-subtle px-4 py-3">
          <button className="btn-ghost" onClick={onClose} disabled={saving}>{t("common.cancel")}</button>
          <button className="btn-primary" onClick={save} disabled={saving}>{t("common.save")}</button>
        </div>
      </div>
    </div>
  );
}
